import { Injectable, signal, inject, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, tap, BehaviorSubject, catchError, map, of } from 'rxjs';
import {
  Account,
  AccountAccess,
  AccountMembership,
  CreateAccountDto,
  UpdateAccountDto,
  InviteMemberDto,
  InviteMemberResponse,
  UpdateMemberRoleDto,
} from '../models/account.model';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private http = inject(HttpClient);

  private readonly API_URL = environment.apiUrl || 'http://localhost:8000/api';
  private readonly ACTIVE_ACCOUNT_KEY = 'fluxtail-active-account';

  // Active account state
  private activeAccountSubject = new BehaviorSubject<string | null>(this.getStoredActiveAccount());
  public activeAccount$ = this.activeAccountSubject.asObservable();

  // User's account memberships
  private membershipsSig = signal<AccountMembership[]>([]);
  public memberships = this.membershipsSig.asReadonly();
  private readonly activeAccountId = toSignal(this.activeAccount$, { requireSync: true });
  readonly access = computed(() => this.memberships().find(
    membership => membership.account_id === this.activeAccountId(),
  )?.account?.access ?? null);
  readonly canWrite = computed(() => this.access()?.can_write === true);
  readonly availableMemberships = computed(() =>
    this.memberships().filter(m => m.active && m.account && m.account.lifecycle_state !== 'closed'),
  );

  switchAccount(accountId: string): void {
    if (accountId === this.getActiveAccountId() ||
        !this.availableMemberships().some(m => m.account_id === accountId)) return;

    this.setActiveAccount(accountId);
    // A full navigation clears account-bound views, filters and in-memory caches.
    window.location.assign('/dashboard');
  }


  /**
   * Get stored active account ID from localStorage
   */
  private getStoredActiveAccount(): string | null {
    return localStorage.getItem(this.ACTIVE_ACCOUNT_KEY);
  }

  /**
   * Set active account (stored in localStorage and emitted to subscribers)
   */
  setActiveAccount(accountId: string): void {
    localStorage.setItem(this.ACTIVE_ACCOUNT_KEY, accountId);
    this.activeAccountSubject.next(accountId);
  }

  /**
   * Get current active account ID
   */
  getActiveAccountId(): string | null {
    this.syncActiveAccountFromStorage();
    return this.activeAccountSubject.value;
  }

  /**
   * Clear active account (on logout)
   */
  clearActiveAccount(): void {
    localStorage.removeItem(this.ACTIVE_ACCOUNT_KEY);
    this.activeAccountSubject.next(null);
    this.membershipsSig.set([]);
  }

  /**
   * Load user's account memberships
   * Should be called after login
   */
  loadMemberships(): Observable<{ memberships: AccountMembership[] }> {
    this.syncActiveAccountFromStorage();
    return this.http
      .get<{ memberships: AccountMembership[] }>(`${this.API_URL}/accounts/memberships`)
      .pipe(
        map((response) => {
          const memberships = this.normalizeMemberships(response.memberships);
          this.storeMemberships(memberships);
          return { memberships };
        }),
        catchError((err) => {
          if (err.status === 404) {
            return this.loadMembershipsFromAccounts();
          }

          this.membershipsSig.set([]);
          return of({ memberships: [] });
        }),
      );
  }

  /**
   * Get all accounts for current user
   */
  getAccounts(): Observable<{ accounts: Account[] }> {
    return this.http
      .get<Account[] | { accounts: Account[] }>(`${this.API_URL}/accounts`)
      .pipe(map((response) => (Array.isArray(response) ? { accounts: response } : response)));
  }

  /**
   * Get specific account details
   */
  getAccount(accountId: string): Observable<{ account: Account }> {
    return this.http.get<{ account: Account }>(`${this.API_URL}/accounts/${accountId}`).pipe(
      tap(({ account }) => {
        if (account.access) this.updateAccess(accountId, account.access, account.plan);
      }),
    );
  }

  updateAccess(accountId: string, access: AccountAccess, plan?: Account['plan']): void {
    this.membershipsSig.update(memberships => memberships.map(membership =>
      membership.account_id === accountId && membership.account
        ? { ...membership, account: { ...membership.account, access, ...(plan ? {plan} : {}) } }
        : membership,
    ));
  }

  /**
   * Create new account (user becomes owner)
   */
  createAccount(data: CreateAccountDto): Observable<{ account: Account }> {
    return this.http.post<{ account: Account }>(`${this.API_URL}/accounts`, data).pipe(
      tap(() => {
        // Reload memberships to include new account
        this.loadMemberships().subscribe();
      }),
    );
  }

  /**
   * Update account settings
   */
  updateAccount(accountId: string, data: UpdateAccountDto): Observable<{ account: Account }> {
    return this.http.put<{ account: Account }>(`${this.API_URL}/accounts/${accountId}`, data).pipe(
      tap((response) => {
        this.membershipsSig.update((memberships) =>
          memberships.map((membership) =>
            membership.account_id === accountId
              ? { ...membership, account: response.account }
              : membership,
          ),
        );
      }),
    );
  }

  /**
   * Delete account (owner only)
   */
  deleteAccount(
    accountId: string,
  ): Observable<{ message: string; account: Account; billing_cancel_pending: boolean }> {
    return this.http
      .delete<{ message: string; account: Account; billing_cancel_pending: boolean }>(
        `${this.API_URL}/accounts/${accountId}`,
      )
      .pipe(
        tap(() => {
          this.loadMemberships().subscribe();
        }),
      );
  }

  restoreAccount(accountId: string): Observable<{ message: string; account: Account; billing_cancellation_remains: boolean }> {
    return this.http
      .post<{ message: string; account: Account; billing_cancellation_remains: boolean }>(
        `${this.API_URL}/accounts/${accountId}/restore`,
        {},
      )
      .pipe(tap(() => this.loadMemberships().subscribe()));
  }

  /**
   * Get account members
   */
  getMembers(accountId: string): Observable<{ members: AccountMembership[] }> {
    return this.http.get<{ members: AccountMembership[] }>(
      `${this.API_URL}/accounts/${accountId}/members`,
    );
  }

  /**
   * Invite user to account
   */
  inviteMember(
    accountId: string,
    data: InviteMemberDto,
  ): Observable<InviteMemberResponse> {
    return this.http.post<InviteMemberResponse>(
      `${this.API_URL}/accounts/${accountId}/members`,
      data,
    );
  }

  /**
   * Update member role
   */
  updateMemberRole(
    accountId: string,
    membershipId: string,
    data: UpdateMemberRoleDto,
  ): Observable<{ membership: AccountMembership }> {
    return this.http.put<{ membership: AccountMembership }>(
      `${this.API_URL}/accounts/${accountId}/members/${membershipId}`,
      data,
    );
  }

  /**
   * Remove member from account
   */
  removeMember(accountId: string, membershipId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.API_URL}/accounts/${accountId}/members/${membershipId}`,
    );
  }

  /**
   * Leave account (user removes themselves)
   */
  leaveAccount(accountId: string): Observable<{ message: string }> {
    return this.http
      .post<{ message: string }>(`${this.API_URL}/accounts/${accountId}/leave`, {})
      .pipe(
        tap(() => {
          // Clear active account if leaving current account
          if (this.getActiveAccountId() === accountId) {
            this.clearActiveAccount();
          }
          // Reload memberships
          this.loadMemberships().subscribe();
        }),
      );
  }

  /**
   * Get current active membership (includes account data)
   */
  getActiveMembership(): AccountMembership | null {
    const activeId = this.getActiveAccountId();
    if (!activeId) return null;

    return this.membershipsSig().find((m) => m.account_id === activeId) || null;
  }

  /**
   * Check if user has role in active account
   */
  hasRole(role: 'owner' | 'admin' | 'member' | 'viewer'): boolean {
    const membership = this.getActiveMembership();
    if (!membership) return false;

    const roleHierarchy = { owner: 4, admin: 3, member: 2, viewer: 1 };
    return roleHierarchy[membership.role] >= roleHierarchy[role];
  }

  /**
   * Check if user can manage resources (admin or owner)
   */
  canManageResources(): boolean {
    return this.hasRole('admin');
  }

  /**
   * Normalize membership payloads to ensure account_id is always present.
   */
  private normalizeMemberships(memberships: AccountMembership[] = []): AccountMembership[] {
    const normalized: AccountMembership[] = [];

    for (const membership of memberships) {
      const accountId = membership.account_id || membership.account?.id;
      if (!accountId) {
        continue;
      }

      const account = membership.account
        ? { ...membership.account, id: membership.account.id ?? accountId }
        : undefined;

      normalized.push({
        ...membership,
        id: membership.id ?? accountId,
        account_id: accountId,
        account,
      });
    }

    return normalized;
  }

  /**
   * Persist memberships state and ensure an active account is selected.
   */
  private storeMemberships(memberships: AccountMembership[]): void {
    this.membershipsSig.set(memberships);
    this.syncActiveAccountFromStorage();

    const activeAccountId = this.activeAccountSubject.value;
    const activeAccountIsAvailable = memberships.some((m) => m.account_id === activeAccountId);

    if (activeAccountId && activeAccountIsAvailable) {
      return;
    }

    const firstAccountId = memberships[0]?.account_id;
    if (firstAccountId) {
      this.setActiveAccount(firstAccountId);
      return;
    }

    this.clearActiveAccount();
  }

  /**
   * Keep in-memory active account aligned with localStorage.
   * Needed because interceptor can clear localStorage directly on 401.
   */
  private syncActiveAccountFromStorage(): void {
    const stored = this.getStoredActiveAccount();
    if (stored === this.activeAccountSubject.value) {
      return;
    }
    this.activeAccountSubject.next(stored);
    if (!stored) {
      this.membershipsSig.set([]);
    }
  }

  /**
   * Fallback loader for environments where /accounts/memberships is unavailable.
   */
  private loadMembershipsFromAccounts(): Observable<{ memberships: AccountMembership[] }> {
    return this.http.get<Account[] | { accounts: Account[] }>(`${this.API_URL}/accounts`).pipe(
      map((response) => {
        const accounts = Array.isArray(response) ? response : (response?.accounts ?? []);
        const memberships: AccountMembership[] = [];

        for (const account of accounts) {
          const membership = this.accountToMembership(account);
          if (membership) {
            memberships.push(membership);
          }
        }

        this.storeMemberships(memberships);
        return { memberships };
      }),
      catchError(() => {
        this.membershipsSig.set([]);
        return of({ memberships: [] });
      }),
    );
  }

  /**
   * Convert an account summary into a membership representation.
   */
  private accountToMembership(account: Account | undefined | null): AccountMembership | null {
    if (!account?.id) {
      return null;
    }

    return {
      id: account.id,
      account_id: account.id,
      role: account.role ?? 'member',
      active: account.active ?? true,
      account: {
        ...account,
        id: account.id,
      },
    };
  }
}
